import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { Wordmark } from '@/components/brand';
import { Button } from '@/components/ui/button';
import { LEGAL_UPDATED_AT, formatLegalDate, LEGAL_VERSION } from '@/features/legal/legal.config';
import { LegalLinks } from '@/features/legal/legal-links';
import { errorMessage } from '@/lib/api';
import { authApi } from '../auth-api';
import { landingFor, needsTermsAcceptance } from '../guards';
import { useSession } from '../session';

/** Tela cheia para quem ainda não aceitou a versão vigente dos Termos de Uso e da Política de Privacidade. */
export function AcceptTermsPage() {
  const { me, setMe, logout } = useSession();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // nada a aceitar (já aceitou, operador ou impersonação): segue para o destino normal
  if (!needsTermsAcceptance(me)) return <Navigate to={landingFor(me, null)} replace />;

  const onAccept = async () => {
    setBusy(true);
    setError(null);
    try {
      const next = await authApi.acceptTerms(me?.legalVersion ?? LEGAL_VERSION);
      setMe(next);
      navigate('/', { replace: true });
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const onLogout = async () => {
    await logout();
    navigate('/entrar', { replace: true });
  };

  return (
    <main id="conteudo" className="grid min-h-dvh place-items-center bg-background px-5 py-10">
      <div className="w-full max-w-md">
        <Wordmark />
        <h1 className="mt-8 font-display text-3xl font-semibold">Termos de Uso e Política de Privacidade</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Versão {me?.legalVersion ?? LEGAL_VERSION}, atualizada em {formatLegalDate(LEGAL_UPDATED_AT)}.
        </p>
        <p className="mt-5 text-sm">
          Para continuar usando o sistema, leia e aceite os Termos de Uso e a Política de Privacidade. Ela explica, entre outros pontos, como os dados de clientes, inclusive os dados de saúde, são tratados.
        </p>
        <LegalLinks newTab className="mt-4 text-sm" linkClassName="font-medium text-primary" />
        {error && (
          <p role="alert" className="mt-5 rounded-control bg-destructive-soft px-3 py-2.5 text-sm text-destructive">
            {error}
          </p>
        )}
        <div className="mt-7 flex flex-col gap-3">
          <Button size="lg" onClick={() => void onAccept()} loading={busy}>
            Aceitar e continuar
          </Button>
          <Button variant="ghost" onClick={() => void onLogout()} disabled={busy}>
            Sair
          </Button>
        </div>
      </div>
    </main>
  );
}
